import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import Sidebar from '../components/Sidebar';
import TopButtons from '../components/common/TopButtons';
import { useAnalytics } from '../hooks/useAnalytics';
import { useAccount, useChainId } from 'wagmi';
import { ethers } from 'ethers'; //
import { useMemo } from 'react'
import { type Config, useConnectorClient } from 'wagmi'
import { clientToSigner } from '../utils/provider'; // 
import Loading from '../components/common/Loading'; 
import { getFactoryAddress, getNativeToken, UPLOAD_URL } from '../config/contracts';
import { useNavigate } from 'react-router-dom';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { useToast } from '../contexts/ToastContext';
import { Tips } from '../components/common/tips';
import { useTranslation } from 'react-i18next';

/** Hook to convert a viem Wallet Client to an ethers.js Signer. */
export function useEthersSigner({ chainId }: { chainId?: number } = {}) {
  const { data: client } = useConnectorClient<Config>({ chainId })
  return useMemo(() => (client ? clientToSigner(client) : undefined), [client])
}

const HomeTopButtons = styled(TopButtons)`
`;

const CreateContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #0a0a1a;
  color: white;
  padding: 10px;
  margin-left: 240px;
  overflow-x: hidden;
  
   @media (max-width: 768px) {
    margin-left: 0;
    padding-bottom: 80px;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: 0 40px;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const BasicSettingContainer = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;
  margin-bottom: 5px;
  align-items: flex-start;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const LeftFormSection = styled.div`
  flex: 1;
  width: 100%;
  min-width: 0;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const RightFormSection = styled.div`
  flex: 1;
  width: 100%;
  min-width: 0;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const SectionTitle = styled.h2`
  color: #4ad0ac;
  font-size: 18px;
  margin-bottom: 20px;
`;

const Form = styled.form`
  margin: 0 auto;
  width: 100%;
  text-align: left;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  width: 100%;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const Label = styled.label`
  display: block;
  color: #4ad0ac;
  margin-bottom: 5px;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: white;
  font-size: 16px;
  box-sizing: border-box;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
  
  &:focus {
    outline: none;
    border-color: #4ad0ac;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: white;
  min-height: 100px;
  font-size: 16px;
  resize: vertical;
  box-sizing: border-box;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
  
  &:focus {
    outline: none;
    border-color: #4ad0ac;
  }
`;

const UploadArea = styled.div`
  width: 100%;
  height: 250px;
  border: 2px dashed rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  position: relative;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    border-color: #4ad0ac;
  }
`;

const UploadContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 2;
  background: rgba(10, 10, 26, 0.8);
`;

const UploadIcon = styled.div`
  margin-bottom: 10px;
  svg {
    width: 24px;
    height: 24px;
    fill: #4ad0ac;
  }
`;

const UploadText = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  margin-bottom: 8px;
`;

const SelectFileButton = styled.button`
  background: none;
  border: 1px solid #4ad0ac;
  color: #4ad0ac;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background: rgba(74, 208, 172, 0.1);
  }
`;

const FileInput = styled.input`
  display: none;
`;

const CreateButton = styled.button`
  width: 100%;
  max-width: 400px;
  padding: 14px;
  background: #4ad0ac;
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background: #3ab090;
  }
`;

const Tiptip = styled.p`
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
  text-align: center;
  margin-top: 10px;
`;

const ShowMoreButton = styled.button`
  background: none;
  border: none;
  color: #4ad0ac;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  margin: 20px 0;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ErrorMessage = styled.div`
  color: #ff4d4d;
  font-size: 12px;
  margin-top: 4px;
`;

const TickerInput = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  
  span {
    position: absolute;
    left: 12px;
    color: white;
    font-size: 16px;
  }
  
  input {
    padding-left: 24px;
  }
`;

const PreviewContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  
  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 6px;
  }
`;

const RequiredMark = styled.span`
  color: #ff4d4d;
  margin-left: 4px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: 20px;
`;

const RewardRankContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const RewardRankHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 5px;
`;

const AddButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  
  svg {
    width: 100%;
    height: 100%;
  }
  
  circle {
    fill: #4ad0ac;
  }
  
  path {
    fill: #0a0a1a;
  }
  
  &:hover {
    opacity: 0.8;
  }
`;

const RewardRankItem = styled.div`
  position: relative;
  width: 100%;
`;

const DeleteButton = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #4ad0ac;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  
  &:hover {
    opacity: 0.8;
  }
`;

const RankText = styled.div`
  color: #888;
  font-size: 12px;
  padding: 0 5px;
`;

const RewardRankGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 40px;
  margin-top: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const FormSection = styled.div`
  width: 100%;
`;

const AdvancedSettingContainer = styled.div`
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 0;
  }
`;

const AdvancedSettingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px;
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const Create: React.FC = () => {
  const { t } = useTranslation();

  const { isConnected, address, chain } = useAccount();
  const [isLoading, setIsLoading] = useState(false); 
  const [chainError, setChainError] = useState<string | null>(null);

  const chainId = useChainId();
  const nativeToken = getNativeToken(chainId);
  const { sendEvent } = useAnalytics();
  const signer = useEthersSigner();
  const { showToast } = useToast();
  const handleConnectWallet = () => {
  };

  const handleBuyToken = () => {
  };
  const [showMore, setShowMore] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [ticker, setTicker] = useState('');
  const [tickerError, setTickerError] = useState('');
  const [description, setDescription] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [telegramLink, setTelegramLink] = useState('');
  const [telegramError, setTelegramError] = useState('');
  const [websiteLink, setWebsiteLink] = useState('');
  const [websiteError, setWebsiteError] = useState('');
  const [twitterLink, setTwitterLink] = useState('');
  const [twitterError, setTwitterError] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [selectedFile, setSelectedFile]: any = useState();
  const [fileType, setFileType] = useState<'image' | 'video' | null>(null);
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [rewardRanks, setRewardRanks] = useState<string[]>(['', '', '', '', '']);
  const [minCap, setMinCap] = useState('');
  const [minCapError, setMinCapError] = useState('');
  const [maxRound, setMaxRound] = useState('');
  const [maxRoundError, setMaxRoundError] = useState('');
  const [roundDuration, setRoundDuration] = useState('');
  const [roundDurationError, setRoundDurationError] = useState('');
  const [rolloverRate, setRolloverRate] = useState('');
  const [rolloverRateError, setRolloverRateError] = useState('');
  const [errors, setErrors] = useState({ logo: '' });
  const { openConnectModal } = useConnectModal();
  const [logoUrl, setLogoUrl] = useState<string>('');

  const navigate = useNavigate();

  const resetForm = () => {
    setName('');
    setTicker('');
    setDescription('');
    setTelegramLink('');
    setWebsiteLink('');
    setTwitterLink('');
    setPreviewUrl('');
    setSelectedFile(null);
    setFileType(null);
    setMinCap('');
    setMaxRound('');
    setRoundDuration('');
    setRolloverRate('');
    setRewardRanks(['', '', '', '', '']);
    
    setNameError('');
    setTickerError('');
    setDescriptionError('');
    setTelegramError('');
    setWebsiteError('');
    setTwitterError('');
    setMinCapError('');
    setMaxRoundError('');
    setRoundDurationError('');
    setRolloverRateError('');
    setFormErrors([]);
    setErrors({ logo: '' });
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // 检查文件大小
      if (file.size > 10 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          logo: t('validation.file.tooLarge')
        }));
        event.target.value = '';
        return;
      }

      const isVideo = file.type.startsWith('video/');
      setFileType(isVideo ? 'video' : 'image');
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setSelectedFile(file);
      setErrors(prev => ({
        ...prev,
        logo: ''
      }));

      // 立即上传
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await fetch(UPLOAD_URL, {
          method: "POST",
          body: formData,
        });
        const resData = await res.json();
        setLogoUrl(resData.url);
      } catch (err) {
        setErrors(prev => ({
          ...prev,
          logo: t('validation.file.uploadFailed')
        }));
        setLogoUrl('');
      }
    }
  };

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleTickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTicker(value);
    
    if (value.length > 10) {
      setTickerError('Ticker cannot be more than 10 characters');
    } else {
      setTickerError('');
    }
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setDescription(value);
    
    if (value.length > 180) {
      setDescriptionError('Description cannot be more than 180 characters');
    } else {
      setDescriptionError('');
    }
  };

  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleTelegramChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTelegramLink(value);
    
    if (value && !value.startsWith('https://t.me/')) {
      setTelegramError(t('validation.links.invalidTelegram'));
    } else {
      setTelegramError('');
    }
  };

  const handleWebsiteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setWebsiteLink(value);
    
    if (value && !isValidUrl(value)) {
      setWebsiteError(t('validation.links.invalidWebsite'));
    } else {
      setWebsiteError('');
    }
  };

  const handleTwitterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTwitterLink(value);
    
    if (value && !value.startsWith('https://x.com/') && !value.startsWith('https://twitter.com/')) {
      setTwitterError(t('validation.links.invalidTwitter'));
    } else {
      setTwitterError('');
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setNameError('');
  };

  const handleAddRank = () => {
    setRewardRanks([...rewardRanks, '']);
  };

  const handleDeleteRank = (index: number) => {
    const newRanks = rewardRanks.filter((_, i) => i !== index);
    setRewardRanks(newRanks);
    // if (rewardRanks.length > 1) {
    // }
  };

  const validateRewardRank = (value: string) => {
    if (!value) return true;
    const num = parseFloat(value);
    if (isNaN(num) || num <= 0 || num > 100) {
      return false;
    }
    return true;
  };

  const validateTotalReward = () => {
    const totalReward = rewardRanks.reduce((sum, rank) => {
      const value = parseFloat(rank) || 0;
      return sum + value;
    }, 0);
    
    const rolloverValue = parseFloat(rolloverRate) || 0;
    const total = totalReward + rolloverValue;
    
    if (total > 100) {
      showToast(t('validation.reward.totalExceeded', { total: total.toFixed(2) }), 'error');
      return false;
    }
    return true;
  };

  const handleRankChange = (index: number, value: string) => {
    if (!validateRewardRank(value)) {
      showToast(t('validation.reward.invalidRange'), 'error');
      return;
    }
    
    const newRanks = [...rewardRanks];
    newRanks[index] = value;
    setRewardRanks(newRanks);
    
    validateTotalReward();
  };

  const handleRolloverRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRolloverRate(value);
    validateRolloverRate(value);
    
    validateTotalReward();
  };

  const validateMinCap = (value: string) => {
    if (!value) {
      setMinCapError(t('validation.minCap.required'));
      return false;
    }
    if (!/^\d+(\.\d{0,2})?$/.test(value)) {
      setMinCapError(t('validation.minCap.invalidFormat'));
      return false;
    }
    setMinCapError('');
    return true;
  };

  const validateMaxRound = (value: string) => {
    if (!value) {
      setMaxRoundError(t('validation.maxRound.required'));
      return false;
    }
    if (!/^\d+$/.test(value)) {
      setMaxRoundError(t('validation.maxRound.invalidFormat'));
      return false;
    }
    setMaxRoundError('');
    return true;
  };

  const validateRoundDuration = (value: string) => {
    if (!value) {
      setRoundDurationError(t('validation.roundDuration.required'));
      return false;
    }
    if (!/^\d+(\.\d{0,2})?$/.test(value)) {
      setRoundDurationError(t('validation.roundDuration.invalidFormat'));
      return false;
    }
    if (parseFloat(value) < 0.1) {
      setRoundDurationError(t('validation.roundDuration.tooSmall'));
      return false;
    }
    setRoundDurationError('');
    return true;
  };

  const validateRolloverRate = (value: string) => {
    if (!value) {
      setRolloverRateError(t('validation.rolloverRate.required'));
      return false;
    }
    const num = parseFloat(value);
    if (isNaN(num) || num < 10 || num > 100) {
      setRolloverRateError(t('validation.rolloverRate.invalidRange'));
      return false;
    }
    setRolloverRateError('');
    return true;
  };

  const handleMinCapChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMinCap(value);
    validateMinCap(value);
  };

  const handleMaxRoundChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMaxRound(value);
    validateMaxRound(value);
  };

  const handleRoundDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRoundDuration(value);
    validateRoundDuration(value);
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];
    
    if (!name.trim()) {
      errors.push(t('validation.name.required'));
      setNameError(t('validation.name.required'));
    }
    
    if (!ticker.trim()) {
      errors.push(t('validation.ticker.required'));
      setTickerError(t('validation.ticker.required'));
    } else if (ticker.length > 10) {
      errors.push(t('validation.ticker.invalid'));
      setTickerError(t('validation.ticker.invalid'));
    }
    
    if (description.length > 180) {
      errors.push(t('errors.invalidDescription'));
      setDescriptionError(t('errors.invalidDescription'));
    }

    if (!previewUrl) {
      errors.push(t('validation.file.required'));
      setErrors(prev => ({
        ...prev,
        logo: t('validation.file.required')
      }));
    }

    if (!validateMinCap(minCap)) errors.push(t('errors.invalidMinCap'));
    if (!validateMaxRound(maxRound)) errors.push(t('errors.invalidMaxRound'));
    if (!validateRoundDuration(roundDuration)) errors.push(t('errors.invalidRoundDuration'));
    if (!validateRolloverRate(rolloverRate)) errors.push(t('errors.invalidRolloverRate'));
    
    // 验证所有奖励值
    const hasInvalidReward = rewardRanks.some(rank => {
      if (!rank) return false; // 允许空值
      return !validateRewardRank(rank);
    });
    
    if (hasInvalidReward) {
      errors.push(t('errors.invalidReward'));
    }
    
    // 验证总和
    if (!validateTotalReward()) {
      errors.push(t('errors.totalRewardExceeded'));
    }

    setFormErrors(errors);
    return errors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!chain) {
      showToast(t('errors.connectWallet'), 'error');
      return;
    }

    if (isConnected && address && signer) {
      // Check if the current chain supports game creation
      const factoryAddress = getFactoryAddress(chain.id);
      if (!factoryAddress) {
        showToast(t('errors.contractNotDeployed'), 'error');
        return;
      }

      if (validateForm()) {
        try {
          setIsLoading(true);

          let finalLogoUrl = logoUrl;
          if (!finalLogoUrl && selectedFile) {
            const formData = new FormData();
            formData.append("file", selectedFile);
            const res = await fetch(UPLOAD_URL, {
              method: "POST",
              body: formData,
            });
            const resData = await res.json();
            finalLogoUrl = resData.url;
            setLogoUrl(finalLogoUrl);
          }

          // 将小时转换为秒
          const roundDurationInSeconds = Math.floor(parseFloat(roundDuration) * 3600);
          
          // 处理奖励排名，将百分比转换为万分之一
          const processedRewardRanks = rewardRanks
            .filter(rank => rank !== '') // 过滤掉空值
            .map(rank => Math.floor(parseFloat(rank) * 100)); // 转换为万分之一
          
          // 处理回滚率，将百分比转换为万分之一
          const processedRolloverRate = Math.floor(parseFloat(rolloverRate) * 100);

          // 检查合约地址

          const contract = new ethers.Contract(
            factoryAddress,
            [
              'function createGame(' +
              'string memory name, ' +
              'string memory symbol, ' +
              'string memory desc, ' +
              'string memory logoUrl, ' +
              'string memory tgLink, ' +
              'string memory webLink, ' +
              'string memory xLink, ' +
              'uint256 minCapBNB, ' +
              'uint256 roundDuration, ' +
              'uint256 totalRounds, ' +
              'uint256[] memory rewardRanks, ' +
              'uint256 rollOverRate' +
              ') external returns (address)'
            ],
            signer
          );

          // 准备交易参数
          const txParams = [
            name,
            ticker,
            description || '',
            finalLogoUrl,
            telegramLink || '',
            websiteLink || '',
            twitterLink || '',
            ethers.parseEther(minCap),
            roundDurationInSeconds,
            parseInt(maxRound),
            processedRewardRanks,
            processedRolloverRate
          ];

          try {
            const tx = await contract.createGame(...txParams);
            const receipt = await tx.wait();
            
            const gameCreatedEvent = receipt.logs.find((log: { topics: string[] }) => 
              log.topics[0] === '0xdd35f60fc254a0494611162e741bba2adee08bbe0c91e4c6cb72362fffd4fb10'
            );
            
            if (gameCreatedEvent) {
              const gameAddress = gameCreatedEvent.topics[1];
              const actualAddress = '0x' + gameAddress.slice(-40);
              showToast(`Game created successfully`, 'success');
              
              //reset form
              resetForm();
              navigate(`/game?address=${actualAddress}`);
              // navigate(`/gametest?address=${actualAddress}`);

            } else {
              throw new Error('Game creation event not found in transaction logs');
            }
          } catch (staticError: any) {
            console.error('Static call failed:', staticError);
          }

        } catch (error: any) {
          console.error('Transaction error:', error);
          
          let errorMessage = "Failed to create game. ";
          if (error.code === 'ACTION_REJECTED') {
            errorMessage += "Transaction was rejected by user.";
          } else if (error.code === 'INSUFFICIENT_FUNDS') {
            errorMessage += "Insufficient funds to complete the transaction.";
          } else if (error.message) {
            errorMessage += error.message;
          }
          
          showToast(errorMessage, 'error');
        } finally {
          setIsLoading(false);
        }
      } else {
        console.log('Form validation failed:', formErrors);
      }
    } else {
      openConnectModal?.();
      return;
    }
  };

  return (
    <>
      <Sidebar />
      <CreateContainer>
        <HomeTopButtons 
          onFirstButtonClick={handleConnectWallet}
          onSecondButtonClick={handleBuyToken}
        />
        <Form onSubmit={handleSubmit}>
          <FormContainer>
            <BasicSettingContainer>
              <LeftFormSection>
                <SectionTitle>{t('create.basicSetting')}</SectionTitle>
                <FormGroup>
                  <Label>
                    {t('create.name')}
                    <RequiredMark>*</RequiredMark>
                  </Label>
                  <InputWrapper>
                    <Input 
                      type="text"
                      value={name}
                      onChange={handleNameChange}
                    />
                  </InputWrapper>
                  {nameError && <ErrorMessage>{nameError}</ErrorMessage>}
                </FormGroup>
                
                <FormGroup>
                  <Label>
                    {t('create.ticker')}
                    <RequiredMark>*</RequiredMark>
                  </Label>
                  <TickerInput>
                    <span>$</span>
                    <Input 
                      type="text"
                      value={ticker}
                      onChange={handleTickerChange}
                    />
                  </TickerInput>
                  {tickerError && <ErrorMessage>{tickerError}</ErrorMessage>}
                </FormGroup>
                
                <FormGroup>
                  <Label>{t('create.description')}</Label>
                  <TextArea 
                    value={description}
                    onChange={handleDescriptionChange}
                    maxLength={180}
                  />
                  {descriptionError && <ErrorMessage>{descriptionError}</ErrorMessage>}
                </FormGroup>
              </LeftFormSection>

              <RightFormSection>
                <SectionTitle style={{ visibility: 'hidden' }}>Placeholder</SectionTitle>
                <FormGroup>
                  <Label>
                    {t('create.imageOrVideo')}
                    <RequiredMark>*</RequiredMark>
                  </Label>
                  <UploadArea onClick={handleFileSelect}>
                    {previewUrl && (
                      <PreviewContainer>
                        {fileType === 'video' ? (
                          <video 
                            src={previewUrl} 
                            controls
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <img 
                            src={previewUrl} 
                            alt="Preview" 
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        )}
                      </PreviewContainer>
                    )}
                    <UploadContent>
                      <UploadIcon>
                        <svg viewBox="0 0 24 24">
                          <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                        </svg>
                      </UploadIcon>
                      <UploadText>{t('create.selectFileTip')}</UploadText>
                      <SelectFileButton type="button">{t('create.selectFile')}</SelectFileButton>
                    </UploadContent>
                  </UploadArea>
                  {errors.logo && <ErrorMessage>{errors.logo}</ErrorMessage>}
                  <FileInput 
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleFileChange}
                  />
                </FormGroup>
              </RightFormSection>
            </BasicSettingContainer>

            <AdvancedSettingContainer>
              <SectionTitle>{t('create.advancedSetting')}<RequiredMark>*</RequiredMark></SectionTitle>
              <FormSection>
                <AdvancedSettingGrid>
                  <FormGroup>
                    <Label>{t('create.minCap', { token: nativeToken })}
                      <Tips tipText={t('create.minCapTip')} />
                    </Label>
                    <InputWrapper>
                      <Input 
                        type="text" 
                        placeholder={t('create.placeholder.minCap')} 
                        value={minCap}
                        onChange={handleMinCapChange}
                      />
                    </InputWrapper>
                    {minCapError && <ErrorMessage>{minCapError}</ErrorMessage>}
                  </FormGroup>

                  <FormGroup>
                    <Label>{t('create.maxRound')}
                      <Tips tipText={t('create.maxRoundTip')} />
                    </Label>
                    <InputWrapper>
                      <Input 
                        type="text" 
                        placeholder={t('create.placeholder.maxRound')} 
                        value={maxRound}
                        onChange={handleMaxRoundChange}
                      />
                    </InputWrapper>
                    {maxRoundError && <ErrorMessage>{maxRoundError}</ErrorMessage>}
                  </FormGroup>

                  <FormGroup>
                    <Label>{t('create.roundDuration')}
                      <Tips tipText={t('create.roundDurationTip')} />
                    </Label>
                    <InputWrapper>
                      <Input 
                        type="text" 
                        placeholder={t('create.placeholder.roundDuration')} 
                        value={roundDuration}
                        onChange={handleRoundDurationChange}
                      />
                    </InputWrapper>
                    {roundDurationError && <ErrorMessage>{roundDurationError}</ErrorMessage>}
                  </FormGroup>

                  <FormGroup>
                    <Label>{t('create.rolloverRate')}
                      <Tips tipText={t('create.rolloverRateTip')} />
                    </Label>
                    <InputWrapper>
                      <Input 
                        type="text" 
                        placeholder={t('create.placeholder.rolloverRate')} 
                        value={rolloverRate}
                        onChange={handleRolloverRateChange}
                      />
                    </InputWrapper>
                    {rolloverRateError && <ErrorMessage>{rolloverRateError}</ErrorMessage>}
                  </FormGroup>
                </AdvancedSettingGrid>

                <FormGroup>
                  <RewardRankHeader>
                    <Label>{t('create.rewardRanks')}
                      <Tips tipText={t('create.rewardRanksTip')} />
                    </Label>
                    <AddButton type="button" onClick={handleAddRank}>
                      <svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="10" cy="10" r="10"/>
                        <path d="M14 9h-3V6c0-.55-.45-1-1-1s-1 .45-1 1v3H6c-.55 0-1 .45-1 1s.45 1 1 1h3v3c0 .55.45 1 1 1s1-.45 1-1v-3h3c.55 0 1-.45 1-1s-.45-1-1-1z"/>
                      </svg>
                    </AddButton>
                  </RewardRankHeader>
                  <RewardRankGrid>
                    {rewardRanks.map((rank, index) => (
                      <RewardRankItem key={index}>
                        <InputWrapper>
                          <Input 
                            type="text" 
                            placeholder={t('create.placeholder.rank', { index: index + 1 })}
                            value={rank}
                            onChange={(e) => handleRankChange(index, e.target.value)}
                          />
                          <DeleteButton 
                            type="button" 
                            onClick={() => handleDeleteRank(index)}
                          >
                            <img src="/images/del.svg" alt="Delete" />
                          </DeleteButton>
                        </InputWrapper>
                      </RewardRankItem>
                    ))}
                  </RewardRankGrid>
                </FormGroup>
              </FormSection>
            </AdvancedSettingContainer>

            <ShowMoreButton 
              type="button" 
              onClick={() => setShowMore(!showMore)}
            >
              {showMore ? t('create.hideMore') : t('create.showMore')}
            </ShowMoreButton>
            
            {showMore && (
              <>
                <FormGroup>
                  <Label>{t('create.telegramLink')}</Label>
                  <InputWrapper>
                    <Input 
                      type="text" 
                      placeholder={t('create.placeholder.optional')} 
                      value={telegramLink}
                      onChange={handleTelegramChange}
                    />
                  </InputWrapper>
                  {telegramError && <ErrorMessage>{telegramError}</ErrorMessage>}
                </FormGroup>
                
                <FormGroup>
                  <Label>{t('create.websiteLink')}</Label>
                  <InputWrapper>
                    <Input 
                      type="text" 
                      placeholder={t('create.placeholder.optional')} 
                      value={websiteLink}
                      onChange={handleWebsiteChange}
                    />
                  </InputWrapper>
                  {websiteError && <ErrorMessage>{websiteError}</ErrorMessage>}
                </FormGroup>
                
                <FormGroup>
                  <Label>{t('create.twitterLink')}</Label>
                  <InputWrapper>
                    <Input 
                      type="text" 
                      placeholder={t('create.placeholder.optional')} 
                      value={twitterLink}
                      onChange={handleTwitterChange}
                    />
                  </InputWrapper>
                  {twitterError && <ErrorMessage>{twitterError}</ErrorMessage>}
                </FormGroup>
              </>
            )}

            <ButtonContainer>
              <CreateButton type="submit">{t('create.createCoin')}</CreateButton>
              <Tiptip>{t('create.createTip')}</Tiptip>
            </ButtonContainer>
          </FormContainer>
        </Form>
        {isLoading && <Loading />}
      </CreateContainer>
    </>
  );
};

export default Create; 